import { Injectable, computed, inject, signal } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppNotification } from '../models/app-notification.model';
import { AuthService } from '@features/auth/services/auth.service';
import { InvoiceService } from '@features/finance/services/invoice.service';
import { ProjectService } from '@features/projects/services/project.service';
import { InvoiceStatus } from '@features/finance/models/invoice-status.model';
import { AppIntegrationService } from '@features/settings/services/app-integration.service';
import { DesktopNotificationService } from './desktop-notification.service';

const STORAGE_KEY = 'app_notifications_read';

@Injectable({ providedIn: 'root' })
export class AppNotificationsService {
  private readonly authService = inject(AuthService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly projectService = inject(ProjectService);
  private readonly integrationService = inject(AppIntegrationService);
  private readonly desktopNotificationService = inject(DesktopNotificationService);

  private _notifications = signal<AppNotification[]>([]);
  readonly notifications = computed(() => this._notifications());
  readonly unreadCount = computed(() => this._notifications().filter(n => !n.isRead).length);

  private readIds = new Set<string>(
    JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
  );

  load(): void {
    forkJoin({
      invoices: this.invoiceService.getInvoices().pipe(catchError(() => of([]))),
      projects: this.projectService.getProjects().pipe(catchError(() => of([]))),
      todayEvents: this.integrationService.getTodayEvents().pipe(catchError(() => of([])))
    }).subscribe(({ invoices, projects, todayEvents }) => {
      const now = new Date();
      const result: AppNotification[] = [];

      // Overdue invoices
      invoices
        .filter(inv => inv.status === InvoiceStatus.Overdue)
        .forEach(inv => {
          const id = `overdue-${inv.id}`;
          result.push({
            id,
            type: 'overdue',
            icon: 'warning',
            titleKey: 'notifications.overdueInvoice.title',
            messageKey: 'notifications.overdueInvoice.message',
            messageParams: { number: inv.invoiceNumber, amount: inv.amountDue, currency: (inv as any).currency || '' },
            link: `/finance/invoices/${inv.id}`,
            createdAt: new Date(inv.dueDate),
            isRead: this.readIds.has(id)
          });
        });

      // Due soon (≤7 days, Sent status)
      invoices
        .filter(inv => {
          if (inv.status !== InvoiceStatus.Sent) return false;
          const due = new Date(inv.dueDate);
          const diff = (due.getTime() - now.getTime()) / 86400000;
          return diff >= 0 && diff <= 7;
        })
        .forEach(inv => {
          const due = new Date(inv.dueDate);
          const days = Math.ceil((due.getTime() - now.getTime()) / 86400000);
          const id = `due-soon-${inv.id}`;
          result.push({
            id,
            type: 'due_soon',
            icon: 'schedule',
            titleKey: 'notifications.dueSoon.title',
            messageKey: days === 0 ? 'notifications.dueSoon.today' : 'notifications.dueSoon.message',
            messageParams: { number: inv.invoiceNumber, days },
            link: `/finance/invoices/${inv.id}`,
            createdAt: now,
            isRead: this.readIds.has(id)
          });
        });

      // Projects ending soon (≤7 days, not completed)
      projects
        .filter(p => {
          if (!p.endDate || (p as any).isCompleted) return false;
          const end = new Date(p.endDate);
          const diff = (end.getTime() - now.getTime()) / 86400000;
          return diff >= 0 && diff <= 7;
        })
        .forEach(p => {
          const end = new Date(p.endDate!);
          const days = Math.ceil((end.getTime() - now.getTime()) / 86400000);
          const id = `project-deadline-${p.id}`;
          result.push({
            id,
            type: 'project_deadline',
            icon: 'folder_open',
            titleKey: 'notifications.projectDeadline.title',
            messageKey: 'notifications.projectDeadline.message',
            messageParams: { name: p.name, days },
            link: `/projects/detail/${p.id}`,
            createdAt: now,
            isRead: this.readIds.has(id)
          });
        });

      // Today's Google Calendar meetings
      todayEvents.forEach(ev => {
        const start = new Date(ev.start);
        const id = `gcal-${ev.id}`;
        result.push({
          id,
          type: 'meeting',
          icon: 'event',
          titleKey: 'notifications.meeting.title',
          messageKey: 'notifications.meeting.message',
          messageParams: {
            title: ev.title,
            time: start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          },
          link: ev.htmlLink ?? undefined,
          createdAt: start,
          isRead: this.readIds.has(id)
        });
      });

      // Profile incomplete (no PayPal or bank)
      const user = this.authService.user();
      const bd = (user as any)?.businessDetails;
      if (bd && (!bd.payPalMeUsername && !bd.bankName)) {
        const id = 'profile-payment';
        result.push({
          id,
          type: 'profile',
          icon: 'account_balance',
          titleKey: 'notifications.profile.paymentTitle',
          messageKey: 'notifications.profile.paymentMessage',
          link: '/business-settings',
          createdAt: now,
          isRead: this.readIds.has(id)
        });
      }

      // Business details incomplete
      const hasCompleteBusinessDetails = bd &&
        bd.businessName && bd.email && bd.phone &&
        bd.address && bd.city && bd.countryCode;
      if (user && !hasCompleteBusinessDetails) {
        const id = 'profile-business';
        result.push({
          id,
          type: 'profile',
          icon: 'manage_accounts',
          titleKey: 'notifications.profile.businessTitle',
          messageKey: 'notifications.profile.businessMessage',
          link: '/setup-profile',
          createdAt: now,
          isRead: this.readIds.has(id)
        });
      }

      // Sort: unread first, then by date desc
      result.sort((a, b) => {
        if (a.isRead !== b.isRead) return a.isRead ? 1 : -1;
        return b.createdAt.getTime() - a.createdAt.getTime();
      });

      this._notifications.set(result);
      this.desktopNotificationService.notifyNew(result);
    });
  }

  markAllAsRead(): void {
    const updated = this._notifications().map(n => ({ ...n, isRead: true }));
    updated.forEach(n => this.readIds.add(n.id));
    this._notifications.set(updated);
    this.persistReadIds();
  }

  markAsRead(id: string): void {
    this.readIds.add(id);
    this._notifications.update(list =>
      list.map(n => n.id === id ? { ...n, isRead: true } : n)
    );
    this.persistReadIds();
  }

  private persistReadIds(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...this.readIds]));
  }
}
